// ItemSheet (Domain/Price book, DEC-019): one price book item, as the office opens it beside the list
// (in a FormSheet, the page's): its picture and its state, the book's name (with its code and kind),
// what the customer reads, the price (and for the owner alone our cost and the margin), the member
// price, what it's made of, what usually goes with it, where it sits and its history. Who looks
// changes it: the owner and the office manager edit; a CSR suggests (her changes go as a draft for an
// OK, and she never types a price). Jev's customer words wait dashed until someone uses them or changes
// them first. A new item starts from a similar one, its price empty. The words section stands alone in
// the review queue (ItemWords, `inQueue`), without its own links: the queue's foot approves.
// The app's book fills in what it keeps and leaves out what it doesn't: how it's sold (unit, tax,
// offered) and why the change, beside the price; a place to move it to; its own picture, parts and
// history drawn by the app (slots); and a read-only sheet for who can't change it here, the facts as words.
// Reads only --pbsheet-*; every piece reads its own.
import type { ReactNode } from 'react';
import { Checkbox } from '../../components/Checkbox/Checkbox.tsx';
import { SelectField, TextField } from '../../components/Field/Field.tsx';
import { Glyph } from '../../components/Glyph/Glyph.tsx';
import { KeyValue } from '../../components/KeyValue/KeyValue.tsx';
import { LineTable } from '../../components/LineTable/LineTable.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { Picture, type PictureSrc, type PictureState } from '../../components/Picture/Picture.tsx';
import './ItemSheet.css';

export type SheetViewer = 'owner' | 'office' | 'csr';
export interface SheetWords {
  /** approved; draft is Jev's, waiting for an OK */
  state: 'approved' | 'draft';
  name: string;
  what: string;
  included: string[];
  warranty: string | null;
  /** The fields the book keeps, in their order; all four when not said. */
  fields?: WordsField[];
}
export type WordsField = 'name' | 'what' | 'included' | 'warranty';
const ALL_WORDS: WordsField[] = ['name', 'what', 'included', 'warranty'];
export interface SheetItem {
  code: string;
  /** "Equipment" */
  kind: string;
  name: string;
  picture: { src: PictureSrc | null; state: PictureState; line: string };
  words: SheetWords | null;
  /** "1,450.00"; empty for a new item */
  price: string;
  member: string;
  /** The owner's alone */
  cost?: string;
  margin?: { text: string; isBad?: boolean };
  madeOf: { name: string; quantity: string; cost?: string }[];
  soldHours?: string;
  /** What usually goes with it; undefined when the book doesn't keep it (the section goes). */
  goes?: { id: string; name: string; picture: PictureSrc | null; line: string }[];
  /** Where it sits (its category's path, and its id when it can move), and how it sells. */
  place: { path: string; sold?: string; categoryId?: string };
  history: { id: string; when: string; who: string; what: string; why: string }[];
  /** How it's sold, when the book keeps it: the unit, its tax and whether it's offered. */
  sell?: { unit: string; taxable: boolean; taxClass: string; active: boolean };
  /** Why the change, kept in its history (the app's book asks for one with every save). */
  reason?: string;
}

export interface ItemSheetProps {
  item: SheetItem;
  viewer: SheetViewer;
  /** edit: one item open; new: an item being added (it starts from a similar one) */
  mode?: 'edit' | 'new';
  /** Who changes prices, said to a CSR ("Nora, or someone she names"). */
  pricesBy: string;
  /** How many more of Jev's drafts are waiting (See the other 3 waiting). */
  othersWaiting?: number;
  onReview?: () => void;
  /** A new item: what it starts from. */
  similar?: { value: string; options: { value: string; label: string }[]; onChange: (v: string) => void };
  onChange?: (field: string, value: string) => void;
  /** Read only here (an imported item, or who can't change the book): the facts as words. */
  readOnly?: boolean;
  /** Where it can move to: Where it sits becomes a choice. */
  places?: { value: string; label: string }[];
  /** The tax classes, for How it's sold. */
  taxClasses?: { value: string; label: string }[];
  /** A new item: its kinds, to pick one, and a code to type. */
  kinds?: { value: string; label: string }[];
  /** What the app draws in a section's place: its pictures, what it's made of, its history. */
  slots?: { picture?: ReactNode; madeOf?: ReactNode; history?: ReactNode };
}

const canEdit = (v: SheetViewer) => v !== 'csr';

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="fl-pbsheet__sec" aria-label={title}>
      <h3 className="fl-pbsheet__h">
        <span>{title}</span>
        {action ? <span className="fl-pbsheet__act">{action}</span> : null}
      </h3>
      {children}
    </section>
  );
}

/** What the customer reads: Jev's draft dashed, the words as fields, or none yet. */
export function ItemWords({
  words,
  viewer,
  inQueue,
  othersWaiting,
  onReview,
  onChange,
  onUse,
  readOnly,
}: {
  words: SheetWords | null;
  viewer: SheetViewer;
  /** The words as they read, no fields. */
  readOnly?: boolean;
  /** In the review queue: no links of its own, the queue's foot approves (critic K23). */
  inQueue?: boolean;
  othersWaiting?: number;
  onReview?: () => void;
  onChange?: (field: string, value: string) => void;
  onUse?: () => void;
}) {
  if (!words)
    return (
      <p className="fl-pbsheet__muted">
        None yet. Jev drafts them once the book’s name and the picture are in.
        {inQueue ? null : (
          <>
            {' '}
            <Link onPress={() => undefined}>Ask Jev to draft them</Link>
          </>
        )}
      </p>
    );
  if (words.state === 'draft')
    return (
      <div className="fl-pbsheet__draft">
        <p className="fl-pbsheet__by">
          <Glyph name="note" className="fl-pbsheet__glyph" />
          Jev’s draft, waiting for an OK
        </p>
        <p className="fl-pbsheet__strong">{words.name}</p>
        <p>{words.what}</p>
        <p className="fl-pbsheet__muted">
          Included: {words.included.join(' · ')}
          {words.warranty ? ` · ${words.warranty}` : ''}
        </p>
        {inQueue ? null : (
          <p className="fl-pbsheet__links">
            {canEdit(viewer) ? (
              <>
                <Link onPress={() => onUse?.()}>Use Jev’s words</Link>
                <Link onPress={() => undefined}>Change them first</Link>
              </>
            ) : (
              <Link onPress={() => undefined}>Suggest a change</Link>
            )}
            {othersWaiting && onReview ? (
              <Link onPress={onReview}>See the other {othersWaiting} waiting</Link>
            ) : null}
          </p>
        )}
      </div>
    );
  const set = (f: string) => (v: string) => onChange?.(f, v);
  const fields = words.fields ?? ALL_WORDS;
  if (readOnly) {
    const included = words.included.filter((l) => l.trim());
    return (
      <div className="fl-pbsheet__stack">
        {fields.includes('name') && words.name ? <p className="fl-pbsheet__strong">{words.name}</p> : null}
        {fields.includes('what') && words.what ? <p>{words.what}</p> : null}
        {fields.includes('included') && included.length ? (
          <ul className="fl-pbsheet__included">
            {included.map((l, n) => (
              <li key={n}>{l}</li>
            ))}
          </ul>
        ) : null}
        {fields.includes('warranty') && words.warranty ? (
          <p className="fl-pbsheet__muted">{words.warranty}</p>
        ) : null}
        {!words.name && !words.what && !included.length && !words.warranty ? (
          <p className="fl-pbsheet__muted">None yet.</p>
        ) : null}
      </div>
    );
  }
  return (
    <div className="fl-pbsheet__fields">
      {fields.includes('name') ? (
        <TextField label="Customer’s name for it" value={words.name} onChange={set('wordsName')} />
      ) : null}
      {fields.includes('what') ? (
        <TextField label="What it is, one line" value={words.what} onChange={set('what')} />
      ) : null}
      {fields.includes('included') ? (
        <TextField
          label="What’s included"
          description="One line each; the customer sees them as ticks."
          multiline
          value={words.included.join('\n')}
          onChange={set('included')}
        />
      ) : null}
      {fields.includes('warranty') ? (
        <TextField label="Warranty" value={words.warranty ?? ''} onChange={set('warranty')} />
      ) : null}
    </div>
  );
}

export function ItemSheet({
  item: i,
  viewer,
  mode = 'edit',
  pricesBy,
  othersWaiting,
  onReview,
  similar,
  onChange,
  readOnly,
  places,
  taxClasses,
  kinds,
  slots = {},
}: ItemSheetProps) {
  const set = (f: string) => (v: string) => onChange?.(f, v);
  const edit = canEdit(viewer) && !readOnly;
  const sell = i.sell;
  const taxWord = (k: string) => taxClasses?.find((t) => t.value === k)?.label ?? k;
  const owner = viewer === 'owner';
  const pic = i.picture;
  return (
    <div className="fl-pbsheet">
      {mode === 'new' && similar ? (
        <SelectField
          label="Start from a similar item"
          description="Its words, parts and suggestions come with it; the price doesn’t."
          value={similar.value}
          onChange={similar.onChange}
          options={similar.options}
        />
      ) : null}
      <Section title="Picture">
        {slots.picture ?? (
          <div className="fl-pbsheet__pic">
            <Picture src={pic.src} state={pic.state} size="hero" alt={pic.src ? i.name : undefined} />
            <div className="fl-pbsheet__stack">
              <p className="fl-pbsheet__muted">{pic.line}</p>
              {edit ? (
                <p className="fl-pbsheet__links" data-stack="">
                  {pic.state === 'to_approve' ? (
                    <>
                      <Link onPress={() => undefined}>Use this one</Link>
                      <Link onPress={() => undefined}>Make another</Link>
                    </>
                  ) : pic.state === 'none' ? (
                    <Link onPress={() => undefined}>Make one</Link>
                  ) : pic.state === 'ready' ? (
                    <Link onPress={() => undefined}>Make its own picture</Link>
                  ) : null}
                  <Link onPress={() => undefined}>Add a photo or spec sheet</Link>
                </p>
              ) : null}
            </div>
          </div>
        )}
      </Section>
      <Section title="The book’s name">
        <div className="fl-pbsheet__fields">
          {!readOnly ? (
            <TextField
              label="Name"
              value={i.name}
              onChange={set('name')}
              placeholder="What the techs search for"
            />
          ) : null}
          {mode === 'new' && edit && kinds ? (
            <div className="fl-pbsheet__row">
              <SelectField label="Kind" value={i.kind} onChange={set('kind')} options={kinds} />
              <TextField
                label="Code"
                value={i.code}
                onChange={set('code')}
                placeholder="Made from the name if empty"
              />
            </div>
          ) : (
            <KeyValue
              density="tight"
              items={[
                ...(readOnly ? [{ label: 'Name', value: i.name }] : []),
                { label: 'Code', value: i.code || 'Given when it’s added' },
                { label: 'Kind', value: i.kind },
              ]}
            />
          )}
        </div>
      </Section>
      <Section title="What the customer reads">
        <ItemWords
          words={i.words}
          viewer={viewer}
          othersWaiting={othersWaiting}
          onReview={onReview}
          onChange={onChange}
          readOnly={readOnly}
        />
      </Section>
      <Section title="Price">
        {edit ? (
          <div className="fl-pbsheet__row">
            <TextField label="Price" value={i.price} onChange={set('price')} placeholder="$0.00" />
            <TextField label="Members pay" value={i.member} onChange={set('member')} placeholder="The same" />
            {owner && i.cost !== undefined ? (
              <TextField label="Our cost" value={i.cost} onChange={set('cost')} placeholder="$0.00" />
            ) : null}
          </div>
        ) : (
          <KeyValue
            items={[
              { label: 'Price', value: i.price ? `$${i.price}` : 'Not set' },
              { label: 'Members pay', value: i.member ? `$${i.member}` : 'The same' },
              ...(readOnly && owner && i.cost !== undefined
                ? [{ label: 'Our cost', value: i.cost ? `$${i.cost}` : '—' }]
                : []),
              ...(readOnly && sell
                ? [
                    { label: 'Sold by', value: sell.unit },
                    { label: 'Tax', value: sell.taxable ? taxWord(sell.taxClass) : 'Not taxed' },
                    { label: 'Offered', value: sell.active ? 'Yes, techs can add it' : 'No' },
                  ]
                : []),
            ]}
          />
        )}
        {edit && sell ? (
          <>
            <div className="fl-pbsheet__row">
              <TextField
                label="Sold by"
                value={sell.unit}
                onChange={set('unit')}
                placeholder="each, hour, foot"
              />
              {taxClasses ? (
                <SelectField
                  label="Tax"
                  value={sell.taxClass}
                  onChange={set('taxClass')}
                  options={taxClasses}
                  isDisabled={!sell.taxable}
                />
              ) : null}
            </div>
            <div className="fl-pbsheet__stack">
              <Checkbox isSelected={sell.taxable} onChange={(on) => onChange?.('taxable', String(on))}>
                Taxable
              </Checkbox>
              {mode === 'edit' ? (
                <Checkbox isSelected={sell.active} onChange={(on) => onChange?.('active', String(on))}>
                  Offered: techs can add it to estimates and invoices
                </Checkbox>
              ) : null}
            </div>
          </>
        ) : null}
        {owner && i.margin ? (
          <p className="fl-pbsheet__margin" data-bad={i.margin.isBad ? '' : undefined}>
            Only you see this · {i.margin.text}
          </p>
        ) : null}
        {edit ? (
          <p className="fl-pbsheet__muted">Sent estimates keep the prices they were sent at.</p>
        ) : readOnly ? null : (
          <p className="fl-pbsheet__muted">Only {pricesBy} changes prices. Suggest one in a note.</p>
        )}
        {edit && i.reason !== undefined && mode === 'edit' ? (
          <TextField
            label="Why the change"
            description="Kept in its history. Customers don’t see it."
            value={i.reason}
            onChange={set('reason')}
            placeholder="Supplier price went up"
          />
        ) : null}
      </Section>
      {slots.madeOf ? (
        <Section title="What it’s made of">{slots.madeOf}</Section>
      ) : i.madeOf.length ? (
        <Section title="What it’s made of">
          <LineTable
            label={`What ${i.name} is made of`}
            columns={[
              { id: 'part', label: 'Part' },
              { id: 'qty', label: 'Qty', align: 'end' },
              ...(owner ? [{ id: 'cost', label: 'Our cost', align: 'end' as const }] : []),
            ]}
            rows={i.madeOf.map((m, n) => ({
              id: String(n),
              cells: { part: m.name, qty: m.quantity, cost: m.cost ?? '' },
            }))}
          />
          {i.soldHours ? <p className="fl-pbsheet__muted">Sold hours: {i.soldHours}</p> : null}
        </Section>
      ) : null}
      {i.goes ? (
        <Section
          title="Usually goes with it"
          action={edit ? <Link onPress={() => undefined}>Add a suggestion</Link> : undefined}
        >
          {i.goes.length ? (
            <ul className="fl-pbsheet__goes">
              {i.goes.map((g) => (
                <li key={g.id} className="fl-pbsheet__go">
                  <Picture src={g.picture} size="xs" />
                  <span className="fl-pbsheet__goname">
                    <span className="fl-pbsheet__strong">{g.name}</span>
                    <span className="fl-pbsheet__muted">{g.line}</span>
                  </span>
                  {edit ? <Link onPress={() => undefined}>Take off</Link> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="fl-pbsheet__muted">Nothing yet. Suggestions come from what sells with it.</p>
          )}
        </Section>
      ) : null}
      <Section
        title="Where it sits"
        action={edit && !places ? <Link onPress={() => undefined}>Move</Link> : undefined}
      >
        {edit && places ? (
          <SelectField
            label="Where it sits"
            hideLabel
            value={i.place.categoryId ?? ''}
            onChange={set('categoryId')}
            options={places}
          />
        ) : (
          <p className="fl-pbsheet__strong">{i.place.path || 'Not in a category'}</p>
        )}
        {i.place.sold ? <p className="fl-pbsheet__muted">{i.place.sold}</p> : null}
      </Section>
      {slots.history ? (
        <Section title="Its history">{slots.history}</Section>
      ) : i.history.length ? (
        <Section title="Its history">
          <LineTable
            label={`${i.name}’s history`}
            columns={[
              { id: 'when', label: 'When' },
              { id: 'what', label: 'What changed' },
              ...(edit ? [{ id: 'act', label: '' }] : []),
            ]}
            rows={i.history.map((h) => ({
              id: h.id,
              cells: {
                when: h.when,
                what: (
                  <>
                    {h.what} · {h.who}
                    {h.why ? <span className="fl-pbsheet__muted"> · {h.why}</span> : null}
                  </>
                ),
                act: <Link onPress={() => undefined}>Put back</Link>,
              },
            }))}
          />
        </Section>
      ) : null}
    </div>
  );
}
